import React, { useEffect, useState } from 'react';
import { Calendar, Eye, FileText, Image as ImageIcon, Mail, Play, Plus, Save, Send, Sparkles, Trash2, X } from 'lucide-react';
import api from '../lib/api';
import HelpCallout from './HelpCallout';

export default function AdminNewsletter() {
  const [drafts, setDrafts] = useState([]);
  const [digests, setDigests] = useState([]);
  const [editing, setEditing] = useState(null);
  const [config, setConfig] = useState({ configured: false, automation_enabled: false, next_friday_run: null });
  const [automationRuns, setAutomationRuns] = useState([]);
  const [runningNow, setRunningNow] = useState(false);
  const [preview, setPreview] = useState(null);
  const [exportData, setExportData] = useState(null);

  function load() {
    api.get('/admin/newsletter/drafts').then((r) => setDrafts(r.data.items));
    api.get('/admin/digests').then((r) => setDigests(r.data.items));
    api.get('/admin/newsletter/config').then((r) => setConfig(r.data));
    api.get('/admin/newsletter/automation-runs').then((r) => setAutomationRuns(r.data.items));
  }
  useEffect(load, []);

  async function fromDigest(id) {
    const r = await api.post(`/admin/newsletter/draft-from-digest/${id}`);
    setEditing(r.data); load();
    suggestImages(r.data);
  }
  async function createBlank() {
    const r = await api.post('/admin/newsletter/drafts', { subject: 'This weekend in Leicestershire', title: '', intro: '', body: '', items: [] });
    setEditing(r.data); load();
  }
  // Auto-match image-less items to venue/event photos in the media library.
  const [suggesting, setSuggesting] = useState(false);
  async function suggestImages(draft) {
    const items = draft?.items || [];
    if (!items.some((it) => !it.image)) return;
    setSuggesting(true);
    try {
      const r = await api.post('/admin/newsletter/suggest-images', { items });
      setEditing((cur) => (cur && cur.id === draft.id ? { ...cur, items: r.data.items } : cur));
    } catch { /* non-fatal */ }
    finally { setSuggesting(false); }
  }
  // Media-library picker popup for item images
  const [pickerFor, setPickerFor] = useState(null);
  const [media, setMedia] = useState([]);
  const [mediaQ, setMediaQ] = useState('');
  async function openPicker(i) {
    setPickerFor(i);
    if (!media.length) {
      try { const r = await api.get('/admin/media'); setMedia(r.data.items || []); } catch { /* ignore */ }
    }
  }
  function pickImage(m) {
    const items = [...(editing.items || [])];
    const { image_flag, ...rest } = items[pickerFor];
    items[pickerFor] = { ...rest, image: m.url, image_alt: m.filename };
    setEditing({ ...editing, items });
    setPickerFor(null);
  }

  async function save(d) {
    await api.patch(`/admin/newsletter/drafts/${d.id}`, {
      subject: d.subject, title: d.title, intro: d.intro, body: d.body,
      items: (d.items || []).map(({ image_flag, ...rest }) => rest),
    });
    load();
  }
  async function del(id) {
    if (!window.confirm('Delete this draft?')) return;
    await api.delete(`/admin/newsletter/drafts/${id}`);
    load(); if (editing?.id === id) setEditing(null);
  }
  async function showPreview(id) {
    const r = await api.get(`/admin/newsletter/drafts/${id}/preview`);
    setPreview(r.data);
  }
  async function send(d) {
    await save(d);
    const r = await api.post(`/admin/newsletter/drafts/${d.id}/send`);
    if (r.data.mocked) {
      alert('MOCKED — no campaign was created. Reason: ' + r.data.reason + '\n\nSet EMAILOCTOPUS_API_KEY and EMAILOCTOPUS_LIST_ID in backend/.env to enable this.');
    } else if (r.data.export) {
      setExportData({ html: r.data.html, subject: d.subject || d.title });
    } else {
      alert('Error: ' + JSON.stringify(r.data));
    }
    load();
  }

  function downloadExport() {
    const blob = new Blob([exportData.html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `newsletter-${new Date().toISOString().slice(0, 10)}.html`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  function updateItem(i, key, val) {
    const items = [...(editing.items || [])];
    items[i] = { ...items[i], [key]: val };
    setEditing({ ...editing, items });
  }
  function addItem() {
    setEditing({ ...editing, items: [...(editing.items || []), { title: '', body: '', link: '', link_text: 'Read more →' }] });
  }
  function removeItem(i) {
    const items = [...editing.items]; items.splice(i, 1); setEditing({ ...editing, items });
  }

  async function runFridayNow() {
    if (!window.confirm('Run the Friday newsletter pipeline right now? This will generate a fresh AI digest, create a draft, and ' + (config.configured ? 'send via EmailOctopus.' : 'do a MOCKED send (no real email goes out).'))) return;
    setRunningNow(true);
    try {
      await api.post('/admin/newsletter/run-friday-now');
      load();
    } catch (e) { alert('Run failed: ' + (e.response?.data?.detail || e.message)); }
    finally { setRunningNow(false); }
  }

  function nextRunHuman() {
    if (!config.next_friday_run) return '—';
    const d = new Date(config.next_friday_run);
    return d.toLocaleString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' }) + ' (UK)';
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="label text-heritage mb-2">Newsletter</div>
          <h1 className="font-display text-4xl">Compose. Preview. Send.</h1>
        </div>
        <div className="flex gap-3 items-center">
          {!config.configured && (
            <span className="text-xs bg-mustard/30 text-charcoal px-3 py-1.5 rounded-full font-semibold" data-testid="newsletter-mocked-badge">
              EmailOctopus MOCKED — set keys in .env
            </span>
          )}
          {config.configured && (
            <span className="text-xs bg-forest/20 text-forest px-3 py-1.5 rounded-full font-semibold">
              EmailOctopus connected
            </span>
          )}
          <button onClick={createBlank} data-testid="newsletter-new-blank" className="bg-admin border border-charcoal/15 px-5 py-2.5 rounded-full text-sm font-semibold inline-flex items-center gap-2 hover:border-charcoal"><Plus size={14}/> Blank</button>
        </div>
      </div>

      {/* Friday automation panel */}

      <HelpCallout
        title="Your email channel — composed in-app, approved and sent inside EmailOctopus."
        description="Drafts here are HTML emails that go to your EmailOctopus list. Friday automation builds one for you each week with the AI digest + 3 weekend events + the most-clicked attraction + the leading Awards nominee + a rotating member spotlight. You can also compose any draft manually from a digest or from blank."
        appearsAt={[
          { label: 'Subscriber inboxes', where: 'after you paste, schedule and approve the exported HTML inside EmailOctopus' },
          { label: 'Newsletter rendering', where: 'the HTML preview button shows exactly what subscribers will see' },
          { label: 'Sign-up triggers', where: 'every form on the public site (homepage, digest page, Final CTA) feeds /admin/subscribers' },
        ]}
        accent={(config.configured ? 'bg-forest' : 'bg-heritage')}
      />

      {/* Friday automation panel — critical functionality, kept visible */}
      <div className="bg-forest text-base rounded-2xl p-6 mb-6" data-testid="friday-automation-panel">
        <div className="flex items-start justify-between flex-wrap gap-4 mb-4">
          <div>
            <div className="label text-mustard mb-2 inline-flex items-center gap-2"><Calendar size={14}/> Auto-Friday newsletter</div>
            <div className="font-display text-2xl mb-1">{config.automation_enabled ? 'Automation is ON' : 'Automation is OFF'}</div>
            <p className="text-base/80 text-sm">Every Friday at 07:00 UK time, an AI digest is generated from this week&apos;s approved events + weather and turned into a newsletter <b>draft</b>. Nothing is sent automatically — you&apos;ll see the draft below (and get an admin notification) so you can review, tweak and hit <b>Send</b> when ready.</p>
          </div>
          <div className="text-right shrink-0">
            <div className="label text-mustard text-xs mb-1">Next run</div>
            <div className="font-display text-lg" data-testid="next-friday-run">{nextRunHuman()}</div>
          </div>
        </div>
        <button onClick={runFridayNow} disabled={runningNow} data-testid="run-friday-now" className="bg-mustard text-charcoal font-semibold px-5 py-2.5 rounded-full inline-flex items-center gap-2 hover:bg-base disabled:opacity-60">
          <Play size={14}/> {runningNow ? 'Building draft…' : 'Generate a fresh draft now'}
        </button>
        {automationRuns.length > 0 && (
          <div className="mt-6 pt-6 border-t border-base/15">
            <div className="label text-mustard mb-3">Recent runs</div>
            <div className="space-y-2">
              {automationRuns.slice(0, 5).map((r) => (
                <div key={r.id} className="flex items-center gap-3 text-sm bg-base/10 rounded-xl px-4 py-2.5" data-testid={`automation-run-${r.id}`}>
                  <span className={`w-2 h-2 rounded-full shrink-0 ${r.stage === 'sent' ? 'bg-mustard' : r.stage === 'errored' || r.stage === 'send_failed' ? 'bg-heritage' : 'bg-base/40'}`}/>
                  <div className="flex-1">
                    {new Date(r.ran_at).toLocaleString('en-GB')} — <strong>{r.stage}</strong>
                    {r.send_result?.mocked && <span className="ml-2 bg-mustard text-charcoal text-xs font-semibold px-2 py-0.5 rounded-full">MOCKED</span>}
                    {r.error && <span className="text-heritage/80 ml-2">{r.error.slice(0, 100)}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Start from digest */}
      <div className="bg-charcoal text-base rounded-2xl p-6 mb-10">
        <div className="label text-mustard mb-2">Quick start</div>
        <p className="mb-4">Turn a weekly AI digest into a newsletter draft. You can then add extra items, edit the body, preview, and send.</p>
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {digests.slice(0, 5).map((d) => (
            <button key={d.id} onClick={() => fromDigest(d.id)} data-testid={`newsletter-from-digest-${d.id}`}
              className="shrink-0 bg-base/10 border border-base/20 rounded-2xl p-4 text-left hover:border-mustard min-w-[280px]">
              <div className="label text-mustard text-xs mb-1">{new Date(d.week_starting).toLocaleDateString('en-GB', { day:'numeric', month:'short' })}</div>
              <div className="font-display text-lg leading-tight">{d.title}</div>
            </button>
          ))}
          {digests.length === 0 && <div className="text-base/60 text-sm">No digests yet — generate one in Weekly Digest first.</div>}
        </div>
      </div>

      {!editing && (
        <div className="space-y-3">
          {drafts.map((d) => (
            <div key={d.id} className="bg-white border border-charcoal/10 rounded-2xl p-5 flex items-center gap-4" data-testid={`newsletter-row-${d.id}`}>
              <Mail size={18} className="text-heritage shrink-0"/>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{d.subject}</div>
                <div className="text-xs text-muted">
                  Created {new Date(d.created_at).toLocaleDateString('en-GB')} ·{' '}
                  {d.exported_at ? <span className="text-forest">Exported to EmailOctopus {new Date(d.exported_at).toLocaleDateString('en-GB')}</span> : d.sent_at ? <span className="text-forest">Sent {new Date(d.sent_at).toLocaleDateString('en-GB')}{d.send_result?.mocked ? ' (mocked)' : ''}</span> : <span className="text-charcoal/60">Draft</span>}
                </div>
              </div>
              <button onClick={() => { setEditing(d); suggestImages(d); }} className="text-sm font-semibold bg-admin border border-charcoal/15 px-4 py-2 rounded-full hover:border-charcoal" data-testid={`newsletter-edit-${d.id}`}>Edit</button>
              <button onClick={() => del(d.id)} className="p-2 text-heritage hover:bg-heritage/5 rounded-lg"><Trash2 size={14}/></button>
            </div>
          ))}
          {drafts.length === 0 && <div className="text-center py-16 text-muted">No newsletters yet.</div>}
        </div>
      )}

      {/* Editor */}
      {editing && (
        <div className="bg-white border border-charcoal/10 rounded-3xl p-8">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-display text-2xl">Editing newsletter</h3>
            <button onClick={() => setEditing(null)} className="p-2 hover:bg-stone rounded-lg"><X size={18}/></button>
          </div>
          <div className="space-y-4">
            <label className="block"><div className="label mb-1.5">Subject line (email subject)</div><input value={editing.subject || ''} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} data-testid="newsletter-subject" className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/></label>
            <label className="block"><div className="label mb-1.5">Headline</div><input value={editing.title || ''} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5 font-display text-lg"/></label>
            <label className="block"><div className="label mb-1.5">Intro</div><input value={editing.intro || ''} onChange={(e) => setEditing({ ...editing, intro: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/></label>
            <label className="block"><div className="label mb-1.5">Body (paragraphs separated by blank line)</div><textarea rows={8} value={editing.body || ''} onChange={(e) => setEditing({ ...editing, body: e.target.value })} className="w-full bg-admin border border-charcoal/15 rounded-xl px-4 py-2.5"/></label>

            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="label">Additional items</div>
                <div className="flex items-center gap-2">
                  <button onClick={() => suggestImages(editing)} disabled={suggesting} data-testid="newsletter-suggest-images" className="text-xs bg-forest text-base px-3 py-1.5 rounded-full inline-flex items-center gap-1 disabled:opacity-60"><ImageIcon size={12}/> {suggesting ? 'Matching images…' : 'Suggest images'}</button>
                  <button onClick={addItem} data-testid="newsletter-add-item" className="text-xs bg-charcoal text-base px-3 py-1.5 rounded-full inline-flex items-center gap-1"><Plus size={12}/> Add item</button>
                </div>
              </div>
              <div className="space-y-3">
                {(editing.items || []).map((it, i) => (
                  <div key={i} className="bg-admin border border-charcoal/10 rounded-2xl p-4 space-y-3" data-testid={`newsletter-item-${i}`}>
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
                      <input value={it.title || ''} onChange={(e) => updateItem(i, 'title', e.target.value)} placeholder="Title" className="md:col-span-3 bg-white border border-charcoal/10 rounded-xl px-3 py-2 text-sm"/>
                      <textarea rows={2} value={it.body || ''} onChange={(e) => updateItem(i, 'body', e.target.value)} placeholder="Short body" className="md:col-span-4 bg-white border border-charcoal/10 rounded-xl px-3 py-2 text-sm"/>
                      <input value={it.link || ''} onChange={(e) => updateItem(i, 'link', e.target.value)} placeholder="https://..." className="md:col-span-3 bg-white border border-charcoal/10 rounded-xl px-3 py-2 text-sm"/>
                      <input value={it.link_text || ''} onChange={(e) => updateItem(i, 'link_text', e.target.value)} placeholder="Link text" className="md:col-span-1 bg-white border border-charcoal/10 rounded-xl px-3 py-2 text-sm"/>
                      <button onClick={() => removeItem(i)} className="md:col-span-1 p-2 text-heritage hover:bg-heritage/5 rounded-lg"><Trash2 size={14}/></button>
                    </div>
                    <div className="flex items-center gap-3">
                      {it.image ? (
                        <img src={it.image} alt="" className="w-14 h-14 rounded-lg object-cover border border-charcoal/10 shrink-0" data-testid={`newsletter-item-thumb-${i}`} />
                      ) : (
                        <div className="w-14 h-14 rounded-lg bg-white border border-dashed border-charcoal/20 inline-flex items-center justify-center shrink-0"><ImageIcon size={16} className="text-charcoal/30"/></div>
                      )}
                      <input value={it.image || ''} onChange={(e) => updateItem(i, 'image', e.target.value)} placeholder="Image URL — auto-matched from your media library, or paste one" className="flex-1 bg-white border border-charcoal/10 rounded-xl px-3 py-2 text-sm" data-testid={`newsletter-item-image-${i}`}/>
                      <button onClick={() => openPicker(i)} data-testid={`newsletter-item-browse-${i}`} className="text-xs font-semibold bg-admin border border-charcoal/15 px-3 py-2 rounded-full hover:border-charcoal shrink-0">Browse library</button>
                      {it.image && (
                        <button onClick={() => { const items = [...editing.items]; const { image, image_alt, ...rest } = items[i]; items[i] = rest; setEditing({ ...editing, items }); }} className="text-xs font-semibold text-heritage hover:underline shrink-0" data-testid={`newsletter-item-image-remove-${i}`}>Remove</button>
                      )}
                      {!it.image && it.image_flag === 'none_found' && (
                        <span className="text-[11px] bg-mustard/30 text-charcoal px-2.5 py-1 rounded-full font-semibold shrink-0" data-testid={`newsletter-item-noimage-${i}`}>No media-library image found for this venue</span>
                      )}
                    </div>
                  </div>
                ))}
                {(editing.items || []).length === 0 && <div className="text-sm text-muted italic">No extra items. Click "Add item" to include extras alongside the digest body.</div>}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-8 pt-6 border-t border-charcoal/10">
            <button onClick={() => save(editing)} data-testid="newsletter-save" className="bg-admin border border-charcoal/15 px-5 py-2.5 rounded-full font-semibold inline-flex items-center gap-2"><Save size={14}/> Save draft</button>
            <button onClick={() => { save(editing); showPreview(editing.id); }} data-testid="newsletter-preview" className="bg-charcoal text-base px-5 py-2.5 rounded-full font-semibold inline-flex items-center gap-2"><Eye size={14}/> Preview HTML</button>
            <button onClick={() => send(editing)} data-testid="newsletter-send" className="bg-heritage text-base px-6 py-2.5 rounded-full font-semibold inline-flex items-center gap-2 ml-auto hover:bg-heritage-deep"><Send size={14}/> {config.configured ? 'Export HTML for EmailOctopus' : 'Send (mocked)'}</button>
          </div>
        </div>
      )}

      {exportData && (
        <div className="fixed inset-0 bg-charcoal/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setExportData(null)}>
          <div className="bg-white rounded-3xl max-w-xl w-full p-8" onClick={(e) => e.stopPropagation()} data-testid="export-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-2xl">Ready for EmailOctopus</h3>
              <button onClick={() => setExportData(null)} className="p-2 hover:bg-stone rounded-lg"><X size={18}/></button>
            </div>
            <p className="text-sm text-muted mb-5">Nothing has been emailed. Download the file below, then finish it inside EmailOctopus where you can edit, schedule and approve the send.</p>
            <ol className="text-sm space-y-2 mb-6 list-decimal list-inside bg-admin rounded-2xl p-4">
              <li><strong>Download the HTML file</strong> (button below)</li>
              <li>In EmailOctopus: <strong>Campaigns → Create campaign</strong></li>
              <li>At the design step choose <strong>&ldquo;Code your own&rdquo;</strong> — NOT the drag-and-drop editor (pasting there shows raw code)</li>
              <li>Open the downloaded file in any text editor, select all, and paste it in — or paste from the clipboard button below</li>
              <li>Preview, then <strong>schedule or send whenever you&apos;re ready</strong></li>
            </ol>
            <div className="flex flex-wrap gap-3">
              <button onClick={downloadExport} data-testid="export-download" className="bg-heritage text-base px-5 py-2.5 rounded-full font-semibold inline-flex items-center gap-2 hover:bg-heritage-deep">
                <FileText size={14}/> Download .html file
              </button>
              <button
                data-testid="export-copy"
                onClick={async () => { try { await navigator.clipboard.writeText(exportData.html); alert('Copied. Remember: paste into the "Code your own" editor in EmailOctopus.'); } catch { alert('Copy blocked by the browser — use the Download button instead.'); } }}
                className="bg-charcoal text-base px-5 py-2.5 rounded-full font-semibold inline-flex items-center gap-2">
                Copy HTML
              </button>
            </div>
          </div>
        </div>
      )}

      {preview && (
        <div className="fixed inset-0 bg-charcoal/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
          <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-charcoal/10 p-4 flex items-center justify-between gap-3">
              <div className="font-semibold truncate">Preview · Subject: {preview.subject}</div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  data-testid="preview-copy-html"
                  onClick={async () => { try { await navigator.clipboard.writeText(preview.html); alert('HTML copied — paste it into a new EmailOctopus campaign (HTML editor).'); } catch { alert('Copy failed — select the HTML manually.'); } }}
                  className="text-xs bg-charcoal text-base px-3 py-1.5 rounded-full font-semibold"
                >Copy HTML</button>
                <button onClick={() => setPreview(null)} className="p-2 hover:bg-stone rounded-lg"><X size={18}/></button>
              </div>
            </div>
            <iframe srcDoc={preview.html} className="w-full" style={{ height: '70vh', border: 0 }} title="preview"/>
          </div>
        </div>
      )}

      {/* Media-library picker */}
      {pickerFor !== null && editing && (
        <div className="fixed inset-0 bg-charcoal/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="media-picker" onClick={() => setPickerFor(null)}>
          <div className="bg-white rounded-3xl max-w-3xl w-full max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="border-b border-charcoal/10 p-4 flex items-center gap-3">
              <div className="font-semibold flex-1 truncate">Pick an image from the media library</div>
              <input value={mediaQ} onChange={(e) => setMediaQ(e.target.value)} placeholder="Search venue or filename…" className="bg-admin border border-charcoal/10 rounded-xl px-3 py-2 text-sm w-52" data-testid="media-picker-search"/>
              <button onClick={() => setPickerFor(null)} data-testid="media-picker-close" className="p-2 hover:bg-stone rounded-lg"><X size={18}/></button>
            </div>
            <div className="p-4 overflow-y-auto grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
              {media.filter((m) => {
                const q = mediaQ.toLowerCase();
                return m.filename.toLowerCase().includes(q) || (m.refs || []).some((r) => r.toLowerCase().includes(q));
              }).map((m) => (
                <button key={m.url} onClick={() => pickImage(m)} className="group text-left" data-testid={`media-picker-item-${m.filename}`}>
                  <img src={m.url} alt={m.filename} loading="lazy" className="w-full aspect-square object-cover rounded-xl border border-charcoal/10 group-hover:border-heritage transition-colors" />
                  <div className="text-[10px] text-charcoal font-semibold truncate mt-1">{(m.refs || [])[0] || m.filename}</div>
                  {(m.refs || []).length > 1 && <div className="text-[9px] text-muted truncate">+ {m.refs.slice(1).join(', ')}</div>}
                </button>
              ))}
              {media.length === 0 && <div className="col-span-full text-sm text-muted italic py-8 text-center">Loading media…</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
